/* 편집기 상태 — 문서(pages)를 직접 고친 뒤 bump() 로 화면을 다시 그린다(프로토타입의 "고치고 renderArtboard" 방식).
   글을 실시간으로 흘려 넣거나 끌어서 옮길 때 객체를 그대로 고칠 수 있어 단순하다. */
import { useSyncExternalStore } from 'react';
import { showcaseApi, type ShowcaseProject, type ShowcaseProjectBody, type PhotoPick } from '../api/showcaseApi';
import { FONT_SETS, THEMES, THEME_LABEL, fontCss, fromThemeCode, sameFont, toCode } from '../model/constants';
import { isHeading } from '../model/layout';
import { TEMPLATES, templateById } from '../model/templates';
import { PAGE_H, PAGE_W, newId, pageId, syncIdSeq, type ShowcaseContent, type El, type Page } from '../model/types';
import { photoSiteOf } from './photos';

export type LogState = 'ok' | 'skip' | 'wait' | 'run' | 'fail';
export interface ChatLog { state: LogState; text: string }
export interface MsgUsage { total: number; input: number; output: number; thinking: number; images: number; calls: number }
export interface ChatMsg {
  role: 'user' | 'ai' | 'system';
  text: string;
  attach?: string[];
  log?: ChatLog[];
  status?: string;
  usage?: MsgUsage;
  undoIndex?: number;
}

class Editor {
  screen: 'home' | 'editor' = 'home';
  pages: Page[] = [];
  cur = 0;
  selectedId: string | null = null;
  title = '제목 없는 시공사례';
  projectId: number | null = null;
  siteId: number | null = null;
  templateId = 'basic';
  themeKey: string | null = null;
  bundle = false;
  publicYn = 'N';
  publicUrl: string | null = null;
  saveStatus = '';
  saveWarn = false;
  lastSaved = '';
  saving = false;
  zoom = 1;
  dockOpen = true;
  /** 대화에 첨부한 요소 (객체 참조 — 페이지를 넘겨도 유지) */
  attached: El[] = [];
  picking = false;
  flash = new Set<string>();
  imaging = new Set<string>();
  typing = new Set<string>();
  /** AI가 글을 받기 전(생각하는 중) — 요소 id → 안내 문구. 첫 글자가 오면 지운다 */
  thinking = new Map<string, string>();
  chat: ChatMsg[] = [];
  busy = false;
  abort: AbortController | null = null;
  undoStack: string[] = [];
  toast = { msg: '', at: 0 };
  /** 사용량 칩을 새로 고치라는 신호 */
  usageTick = 0;
  rev = 0;
  private ls = new Set<() => void>();
  subscribe = (fn: () => void) => {
    this.ls.add(fn);
    return () => { this.ls.delete(fn); };
  };
  bump() {
    this.rev++;
    this.ls.forEach((f) => f());
  }
}
export const ed = new Editor();
export function useEd() {
  useSyncExternalStore(ed.subscribe, () => ed.rev);
  return ed;
}

/* ── 공통 ── */
let toastTimer: ReturnType<typeof setTimeout> | undefined;
export function toast(msg: string) {
  ed.toast = { msg, at: Date.now() };
  ed.bump();
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { ed.toast = { msg: '', at: 0 }; ed.bump(); }, 2200);
}
export const currentPage = () => ed.pages[ed.cur];
export const findElement = (id: string | null) => (id ? currentPage()?.elements.find((e) => e.id === id) || null : null);
export const selectedElm = () => findElement(ed.selectedId);
export const nextZ = () => {
  const p = currentPage();
  return p && p.elements.length ? Math.max(...p.elements.map((e) => e.z)) + 1 : 1;
};
export function locateElement(el: El) {
  for (let i = 0; i < ed.pages.length; i++) if (ed.pages[i].elements.includes(el)) return i;
  return -1;
}
export const currentTemplate = () => templateById(ed.templateId);
export const currentThemeKey = () => ed.themeKey || currentTemplate().theme || 'basic';

export function select(id: string | null) {
  if (ed.selectedId === id) return;
  ed.selectedId = id;
  ed.bump();
}
export function showPage(i: number) {
  if (i < 0 || i >= ed.pages.length) return;
  if (i !== ed.cur) {
    ed.cur = i;
    if (ed.selectedId && !ed.pages[i].elements.some((e) => e.id === ed.selectedId)) ed.selectedId = null;
  }
  ed.bump();
}

/* ── 되돌리기 — AI가 바꾸기 직전 상태, 직접 편집 직전 상태를 쌓아둔다 (같은 요소·같은 속성을 연달아 바꾸면 한 번만) ── */
const snapshot = () => JSON.stringify({ pages: ed.pages, cur: ed.cur, title: ed.title, theme: ed.themeKey });
let lastUndoKey = '', lastUndoAt = 0;
export function rememberUndo(key?: string) {
  const now = Date.now();
  if (key && key === lastUndoKey && now - lastUndoAt < 1500) { lastUndoAt = now; return; }
  lastUndoKey = key || ''; lastUndoAt = now;
  const s = JSON.parse(snapshot());
  s.manual = true;
  ed.undoStack.push(JSON.stringify(s));
  if (ed.undoStack.length > 30) ed.undoStack.shift();
}
/** fn 을 실행해서 페이지가 바뀌었으면, 바뀌기 전 상태를 되돌리기 목록에 넣고 그 AI 메시지에 버튼을 단다 */
export async function withUndo(fn: () => Promise<void>) {
  const before = snapshot(), beforePages = JSON.stringify(ed.pages);
  await fn();
  if (JSON.stringify(ed.pages) === beforePages) return;
  ed.undoStack.push(before);
  if (ed.undoStack.length > 30) ed.undoStack.shift();
  const last = [...ed.chat].reverse().find((m) => m.role === 'ai');
  if (last) last.undoIndex = ed.undoStack.length;
  ed.bump();
}
export function undoLast() {
  const raw = ed.undoStack.pop();
  if (!raw) { toast('되돌릴 변경이 없어요'); return; }
  const s = JSON.parse(raw);
  ed.pages = s.pages;
  ed.cur = Math.min(s.cur, ed.pages.length - 1);
  ed.title = s.title;
  if ('theme' in s) ed.themeKey = s.theme;
  ed.selectedId = null;
  ed.attached = []; // 되돌리면 페이지 객체가 새로 만들어져서 첨부가 끊긴다
  if (s.manual) toast('↩ 되돌렸어요');
  else ed.chat.push({ role: 'system', text: '↩ 방금 AI가 바꾼 내용을 되돌렸어요.' });
  ed.bump();
}

/* ── 요소 ── */
export function change(elm: El, key: string, fn: (e: El) => void) {
  rememberUndo(elm.id + ':' + key);
  fn(elm);
  ed.bump();
}
export function centerOf(w: number, h: number) {
  return { x: Math.round((PAGE_W - w) / 2), y: Math.round((PAGE_H - h) / 2) };
}
export function addAndSelect(el: Omit<El, 'id' | 'z'>) {
  const p = currentPage();
  if (!p) return;
  rememberUndo();
  const full: El = { id: newId(), z: nextZ(), ...el };
  p.elements.push(full);
  ed.selectedId = full.id;
  ed.bump();
}
export function deleteElement(id: string) {
  const p = currentPage();
  if (!p) return;
  p.elements = p.elements.filter((e) => e.id !== id);
  if (ed.selectedId === id) ed.selectedId = null;
  ed.bump();
}
export function bringToFront(elm: El) {
  const p = currentPage();
  elm.z = (p.elements.length ? Math.max(...p.elements.map((e) => e.z)) : 0) + 1;
}
export function sendToBack(elm: El) {
  const p = currentPage();
  elm.z = (p.elements.length ? Math.min(...p.elements.map((e) => e.z)) : 0) - 1;
}
export function duplicateSelected() {
  const elm = selectedElm();
  if (!elm) return;
  rememberUndo();
  const copy: El = JSON.parse(JSON.stringify(elm));
  copy.id = newId(); copy.x = Math.min(PAGE_W - 20, elm.x + 20); copy.y = Math.min(PAGE_H - 20, elm.y + 20); copy.z = nextZ();
  currentPage().elements.push(copy);
  ed.selectedId = copy.id;
  ed.bump();
}

/* ── 페이지 ── */
export function clonePage(p: Page): Page {
  const c: Page = JSON.parse(JSON.stringify(p));
  c.id = pageId();
  c.elements.forEach((e) => { e.id = newId(); delete e.gen; delete e.sample; });
  return c;
}
export function insertPageAfter(page: Page, idx: number) {
  rememberUndo();
  ed.pages.splice(idx + 1, 0, page);
  ed.cur = idx + 1;
  ed.selectedId = null;
  ed.bump();
}
export function duplicatePageAt(i: number) {
  insertPageAfter(clonePage(ed.pages[i]), i);
  toast(`${i + 1}페이지를 복제했어요`);
}
export function deletePageAt(i: number) {
  if (ed.pages.length <= 1) { toast('마지막 페이지는 지울 수 없어요'); return; }
  rememberUndo();
  ed.pages.splice(i, 1);
  if (ed.cur >= ed.pages.length || ed.cur > i) ed.cur = Math.max(0, ed.cur - 1);
  ed.selectedId = null;
  ed.bump();
  toast(`${i + 1}페이지를 지웠어요 — Ctrl+Z로 되돌릴 수 있어요`);
}
export function movePage(from: number, to: number) {
  if (to === from || to === from + 1) return;
  rememberUndo();
  const curPage = ed.pages[ed.cur];
  const [moved] = ed.pages.splice(from, 1);
  if (to > from) to--;
  ed.pages.splice(to, 0, moved);
  ed.cur = ed.pages.indexOf(curPage);
  ed.bump();
}

/* ── 디자인: 색 테마 · 글꼴 세트 · 배경 ──
   지금 테마의 색과 똑같은 값만 새 테마의 같은 자리 색으로 바꾸므로, 직접 넣은 색·스티커·사진은 그대로 */
const norm = (c?: string) => String(c || '').replace(/\s+/g, '').toLowerCase();
const SLOT_ORDER = {
  text: ['head', 'text', 'muted', 'accent', 'onAlt', 'onAltMuted', 'alt', 'bg'],
  fill: ['tint', 'alt', 'accent', 'bg', 'head'],
  page: ['bg', 'alt', 'tint'],
};
type Slots = Record<string, string | number | undefined>;
function mapColor(val: string | undefined, from: Slots, to: Slots, order: string[]) {
  const v = norm(val);
  if (!v) return val;
  const k = order.find((k) => typeof from[k] === 'string' && norm(from[k] as string) === v);
  return k && to[k] ? String(to[k]) : val;
}
export const isThemeHeading = (e: El) => e.role === 'heading' || isHeading(e);
export function applyTheme(newKey: string) {
  const oldKey = currentThemeKey();
  if (newKey === oldKey) return;
  const from = THEMES[oldKey], to = THEMES[newKey];
  if (!from || !to) return;
  rememberUndo();
  const F = from as unknown as Slots, T = to as unknown as Slots;
  const fr = from.radius || 0, tr = to.radius || 0;
  ed.pages.forEach((p) => {
    p.bg = mapColor(p.bg, F, T, SLOT_ORDER.page) || p.bg;
    p.elements.forEach((e) => {
      if (e.type === 'text') {
        e.color = mapColor(e.color, F, T, SLOT_ORDER.text);
        if (e.bg) e.bg = mapColor(e.bg, F, T, SLOT_ORDER.fill);
        // 테마 글꼴(제목·본문)만 새 테마 글꼴로 — 직접 고른 글꼴은 그대로
        if (from.headFont && sameFont(e.font, from.headFont)) e.font = to.headFont || undefined;
        else if (from.bodyFont && sameFont(e.font, from.bodyFont)) e.font = to.bodyFont || undefined;
        else if (!e.font && isThemeHeading(e) && to.headFont) e.font = to.headFont;
      } else if (e.type === 'shape') {
        if (e.bg) e.bg = mapColor(e.bg, F, T, SLOT_ORDER.fill);
        if (e.stroke) e.stroke = mapColor(e.stroke, F, T, SLOT_ORDER.fill.concat(['head']));
      } else if (e.type === 'image' && !e.clip) {
        // 템플릿 사진 모서리 — 자리별 규칙(대표=테마 값, 서브=12까지, 소개·디테일=16까지)대로였던 사진만 새 테마 값으로
        const cap = p.kind === 'overview' || p.kind === 'detail' ? 16 : p.kind === 'room' && e.role === 'sub' ? 12 : Infinity;
        const was = Math.min(fr, cap), will = Math.min(tr, cap);
        const tplPhoto = p.kind && p.kind !== 'closing' && !(p.kind === 'cover' && (e.radius || 0) > 100);
        if (tplPhoto && (e.radius || 0) === was) e.radius = will || undefined;
      }
    });
  });
  ed.themeKey = newKey;
  ed.bump();
  toast(`'${THEME_LABEL[newKey]}' 색 테마로 바꿨어요 — Ctrl+Z로 되돌릴 수 있어요`);
}
export function applyFontSet(i: number) {
  const set = FONT_SETS[i];
  const hf = fontCss(set.head), bf = fontCss(set.body);
  rememberUndo();
  let n = 0;
  ed.pages.forEach((p) => p.elements.forEach((e) => {
    if (e.type !== 'text') return;
    e.font = isThemeHeading(e) ? hf : bf;
    n++;
  }));
  ed.bump();
  toast(`글꼴 세트 '${set.name}'을(를) 글 ${n}곳에 적용했어요`);
}
export function setBg(c: string, all: boolean) {
  rememberUndo();
  (all ? ed.pages : [currentPage()]).forEach((p) => { if (p) p.bg = c; });
  ed.bump();
}

/* ── 저장 · 열기 ── */
function photoPicks(): PhotoPick[] {
  const seen = new Set<number>(), out: PhotoPick[] = [];
  ed.pages.forEach((p) => p.elements.forEach((e) => {
    const id = e.type === 'image' ? e.photoId ?? e.origPhotoId : undefined;
    if (id && !seen.has(id)) { seen.add(id); out.push({ photoId: id, seq: out.length + 1, cover: out.length === 0 }); }
  }));
  return out;
}
/** 시공사례에 쓴 사진의 현장별 사진 수 — 현장을 모르는 사진이 하나라도 있으면 null(서버의 연결을 그대로 둔다) */
function siteUses(): { siteId: number; photoCount: number }[] | null {
  const seen = new Set<number>(), cnt = new Map<number, number>();
  let unknown = false;
  ed.pages.forEach((p) => p.elements.forEach((e) => {
    if (e.type !== 'image') return;
    const id = e.photoId ?? e.origPhotoId;
    if (!id || seen.has(id)) return; // 예시 사진·같은 사진은 세지 않는다
    seen.add(id);
    const site = e.siteId ?? photoSiteOf(id);
    if (!site) { unknown = true; return; }
    if (!e.siteId) e.siteId = site; // 알게 됐으면 문서에도 적어 둔다
    cnt.set(site, (cnt.get(site) || 0) + 1);
  }));
  return unknown ? null : [...cnt].map(([siteId, photoCount]) => ({ siteId, photoCount }));
}
export function contentOf(): ShowcaseContent {
  return { version: 2, pages: ed.pages, templateId: ed.templateId, themeKey: ed.themeKey };
}
function bodyOf(): ShowcaseProjectBody {
  return {
    title: ed.title.trim() || '제목 없는 시공사례',
    siteId: ed.siteId, templateCode: toCode(ed.templateId), themeCode: toCode(currentThemeKey()),
    bundle: ed.bundle, content: contentOf(), photos: photoPicks(), sites: siteUses(),
  };
}
export async function saveNow(force = false, keepalive = false) {
  if (!ed.projectId || ed.saving || !ed.pages.length) return;
  const body = bodyOf();
  const snap = JSON.stringify(body);
  if (!force && snap === ed.lastSaved) return;
  ed.saving = true;
  ed.saveStatus = '저장 중…'; ed.saveWarn = false; ed.bump();
  try {
    await showcaseApi.save(ed.projectId, body, keepalive);
    ed.lastSaved = snap;
    ed.saveStatus = '저장됨';
  } catch {
    ed.saveStatus = '저장 안 됨 — 잠시 뒤 다시 저장해요'; ed.saveWarn = true;
  } finally {
    ed.saving = false;
    ed.bump();
  }
}

function resetSession() {
  ed.cur = 0; ed.selectedId = null; ed.attached = []; ed.picking = false;
  ed.chat = []; ed.undoStack = [];
  ed.flash.clear(); ed.imaging.clear(); ed.typing.clear(); ed.thinking.clear();
}
/** 새 시공사례 — 지금 것을 먼저 저장하고 서버에 새로 만든다(새로 만든 건 비공개) */
export async function startNewCase(opts: { pages: Page[]; title: string; templateId: string; siteId: number | null; bundle: boolean }) {
  await saveNow(true);
  resetSession();
  ed.pages = opts.pages; ed.title = opts.title; ed.templateId = opts.templateId; ed.themeKey = null;
  ed.siteId = opts.siteId; ed.bundle = opts.bundle; ed.publicYn = 'N'; ed.publicUrl = null;
  ed.projectId = null; ed.lastSaved = '';
  const body = bodyOf();
  ed.projectId = await showcaseApi.create(body);
  ed.lastSaved = JSON.stringify(body);
  ed.saveStatus = '저장됨';
  ed.screen = 'editor';
  ed.bump();
  return ed.projectId;
}
export function loadProject(c: ShowcaseProject) {
  resetSession();
  const content = (c.content || { version: 2, pages: [] }) as ShowcaseContent;
  ed.pages = Array.isArray(content.pages) && content.pages.length ? content.pages : [{ id: pageId(), bg: '#FFFFFF', elements: [] }]; // dq-ignore: 문서 페이지 기본 배경
  syncIdSeq(ed.pages);
  ed.title = c.title;
  ed.projectId = c.projectId;
  ed.siteId = c.siteId;
  ed.templateId = content.templateId || (c.templateCode ? (templateFromCode(c.templateCode) ?? 'basic') : 'basic');
  ed.themeKey = content.themeKey ?? fromThemeCode(c.themeCode);
  ed.bundle = c.bundle;
  ed.publicYn = c.publicYn; ed.publicUrl = c.publicUrl;
  ed.lastSaved = JSON.stringify(bodyOf());
  ed.saveStatus = '저장됨'; ed.saveWarn = false;
  ed.screen = 'editor';
  ed.chat.push({ role: 'system', text: INTRO });
  ed.bump();
}
const templateFromCode = (code: string) => TEMPLATES.find((t) => toCode(t.id) === code)?.id ?? null;

export const INTRO = '하고 싶은 걸 말로 하면 AI가 페이지를 직접 고쳐요. 바뀌는 페이지로 넘어가서 글이 써지는 걸 실시간으로 보여주고, 마음에 안 들면 "↩ 되돌리기"(Ctrl+Z)로 되돌릴 수 있어요. 특정 요소만 고치려면 "요소 선택"으로 페이지에서 골라 첨부하세요 — 첨부한 요소만 AI에게 보여서 다른 곳은 바뀌지 않아요.';

export async function setVisibility(on: boolean) {
  if (!ed.projectId) return;
  await saveNow(true);
  try {
    const c = await showcaseApi.setPublic(ed.projectId, on);
    ed.publicYn = c.publicYn; ed.publicUrl = c.publicUrl;
    ed.bump();
    toast(on ? '공개로 바꿨어요 — 링크가 있으면 누구나 볼 수 있어요' : '비공개로 바꿨어요 — 나만 볼 수 있어요');
  } catch {
    toast('공개 설정을 바꾸지 못했어요');
  }
}
export function goHome() {
  if (ed.busy) { toast('AI가 작업 중이에요. 끝나거나 ■ 중지한 뒤에 나가주세요.'); return false; }
  ed.picking = false;
  void saveNow(true);
  ed.screen = 'home';
  ed.bump();
  return true;
}
